function ensureStorageType(input) {
  if (input !== "localStorage" && input !== "sessionStorage") {
    throw new Error(`Unsupported storage type: "${input}".`);
  }
}

function ensureValueType(input) {
  if (
    input !== "string" &&
    input !== "datetime" &&
    input !== "number" &&
    input !== "boolean"
  ) {
    throw new Error(`Unsupported value type: "${input}".`);
  }
}

function serializeValue(value, context) {
  if (value == null) return undefined;

  const { valueType } = context;

  if (valueType === "string") return serializeString(value);
  if (valueType === "number") return serializeNumber(value);
  if (valueType === "datetime") return serializeDatetime(value);
  if (valueType === "boolean") return serializeBoolean(value);
  throw new Error(`Unsupported valueType: "${valueType}".`);
}

function deserializeValue(value, context) {
  const { valueType, defaultValue } = context;

  if (value == null) return defaultValue;

  if (valueType === "string")
    return deserializeString(value, context) ?? defaultValue;
  if (valueType === "number")
    return deserializeNumber(value, context) ?? defaultValue;
  if (valueType === "datetime")
    return deserializeDatetime(value, context) ?? defaultValue;
  if (valueType === "boolean")
    return deserializeBoolean(value, context) ?? defaultValue;
  throw new Error(`Unsupported valueType: "${valueType}".`);
}

function serializeString(value) {
  if (value === "") return undefined;
  return value;
}

function deserializeString(value) {
  if (value === "") return undefined;
  return value;
}

function serializeNumber(value) {
  if (isNaN(value)) return undefined;
  return value.toString();
}

function deserializeNumber(value, context) {
  const parsed = parseFloat(value);
  if (isNaN(parsed)) {
    console.error(`Invalid number str: "${value}"`, context);
    return undefined;
  }
  return parsed;
}

function serializeDatetime(value) {
  if (isNaN(value)) return undefined;

  return value.toISOString();
}

function deserializeDatetime(value, context) {
  const parsed = new Date(value);
  if (isNaN(parsed)) {
    console.error(`Invalid datetime str: "${value}"`, context);
    return undefined;
  }
  return parsed;
}

function serializeBoolean(value) {
  if (value === true) return "true";
  if (value === false) return "false";
  return undefined;
}

function deserializeBoolean(value) {
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}

/**
 * Wrapper around localStorage and sessionStorage that simplifies
 * data loading and updates across tabs.
 */
class ReactiveWebStorage {
  /**
   * @type {Map<string, {
   *   key: string,
   *   storageType: z.infer<typeof StorageType>,
   *   valueType: z.infer<typeof ValueType>,
   *   defaultValue?: unknown,
   * }>}
   */
  keys = new Map();

  getEventBus() {
    return parent.portalGlobalEventBus;
  }

  getStorageByType(storageType) {
    if (storageType === "localStorage") return localStorage;
    if (storageType === "sessionStorage") return sessionStorage;
    throw new Error(`Unsupported storage type: "${storageType}".`);
  }

  registerKey({
    key,
    storageType = "localStorage",
    valueType = "string",
    defaultValue = undefined,
  }) {
    ensureStorageType(storageType);
    ensureValueType(valueType);

    this.keys.set(key, { key, storageType, valueType, defaultValue });

    // if someone registered onUpdate before key is registered, we want to update them asap
    const value = this.get(key);
    this.getEventBus()?.$emit(
      `integrify-portal:reactiveWebStorage:update:${key}`,
      value
    );
  }

  isRegistered(key) {
    return this.keys.has(key);
  }

  getConf(key) {
    const conf = this.keys.get(key);
    if (!conf) throw new Error(`Unknown key: "${key}".`);

    return conf;
  }

  get(key) {
    const conf = this.getConf(key);

    return deserializeValue(
      this.getStorageByType(conf.storageType).getItem(conf.key),
      conf
    );
  }

  set(key, value) {
    const conf = this.getConf(key);
    const storage = this.getStorageByType(conf.storageType);
    const serialized = serializeValue(value, conf);
    if (serialized == null) {
      storage.removeItem(conf.key);
    } else {
      storage.setItem(conf.key, serialized);
    }
    this.getEventBus()?.$emit(
      `integrify-portal:reactiveWebStorage:update:${key}`,
      value ?? conf.defaultValue
    );
  }

  del(key) {
    this.set(key, undefined);
  }

  onUpdate(key, handler) {
    const { unregister } = this.getEventBus()?.$on(
      `integrify-portal:reactiveWebStorage:update:${key}`,
      handler
    );

    if (this.isRegistered(key)) {
      const value = this.get(key);
      handler(value);
    }

    return { unregister };
  }

  onUpdates(keyHandlerMap) {
    const unregisters = [];

    for (const [key, handler] of Object.entries(keyHandlerMap)) {
      const unregister = this.onUpdate(key, handler).unregister;
      unregisters.push(unregister);
    }

    return {
      unregister() {
        for (const fn of unregisters) {
          fn();
        }
      },
    };
  }

  offUpdate(key, handler) {
    this.getEventBus()?.$off(
      `integrify-portal:reactiveWebStorage:update:${key}`,
      handler
    );
  }
}

export default new ReactiveWebStorage();
